import React, { useEffect, useState } from 'react';

import SuperbillService from './SuperbillService';

import PatientAuthorizationService from '../Authorization/authorizationService';

import DiagnosisCombinerService from '../Authorization/diagnosisCombinerService';

import OfficeService from '../office/officeService';
import clinicalNoteService from '../clinicalNotes/clinicalNoteService.js';
import authorizationClinicalProfileService from '../clinicalNotes/authorizationClinicalProfileService.js';
import apiBaseUrl from '../../utils/config.js';

import './SuperbillPage.css';
import './SuperbillClinicalProfile.css';



const DEFAULT_COLUMNS = {

  date: true,

  patient: true,

  dob: true,

  insurance: true,

  doctor: true,

  office: true,

  authorization: true,

  authDates: true,

  totalVisits: true,

  visitsRemaining: true,

  typeVisit: true,

  procedure: true,

  diagnosis: true,

  billed: true,

  notesCompleted: true,

  actions: true,

};



const TYPE_VISIT_OPTIONS = ['visit', 'eval', 'Progress Note', 'Discharge'];

const EMPTY_CLINICAL_PROFILE_MODAL = {
  open: false,
  authId: '',
  superbillId: '',
  patientName: '',
  bodyPartTemplateId: '',
  laterality: '',
  providerId: '',
  providerName: '',
  saving: false,
  error: '',
};



const getTodayString = () => new Date().toISOString().split('T')[0];



const getPastDateString = (daysBack) => {

  const date = new Date();

  date.setDate(date.getDate() - daysBack);

  return date.toISOString().split('T')[0];

};



const SuperbillPage = () => {

  const today = getTodayString();



  const [dateMode, setDateMode] = useState('single');

  const [selectedDate, setSelectedDate] = useState(today);

  const [startDate, setStartDate] = useState(today);

  const [endDate, setEndDate] = useState(today);



  const [rows, setRows] = useState([]);

  const [pageInfo, setPageInfo] = useState({

    page: 0,

    size: 25,

    totalPages: 0,

    totalElements: 0,

  });



  const [loading, setLoading] = useState(false);

  const [bulkLoading, setBulkLoading] = useState(false);

  const [expanded, setExpanded] = useState({});

  const [error, setError] = useState('');

  const [successMessage, setSuccessMessage] = useState('');
  const [clinicalNoteLoadingBySuperbill, setClinicalNoteLoadingBySuperbill] = useState({});
  const [activeBodyParts, setActiveBodyParts] = useState([]);
  const [clinicalProfileModal, setClinicalProfileModal] = useState({
    ...EMPTY_CLINICAL_PROFILE_MODAL,
  });



  const [viewMode, setViewMode] = useState('cards');

  const [officeFilter, setOfficeFilter] = useState('');

  const [offices, setOffices] = useState([]);



  const [patientSearch, setPatientSearch] = useState('');

  const [debouncedPatientSearch, setDebouncedPatientSearch] = useState('');



  const [insuranceSearch, setInsuranceSearch] = useState('');

  const [debouncedInsuranceSearch, setDebouncedInsuranceSearch] = useState('');



  const [billedFilter, setBilledFilter] = useState('');

  const [notesFilter, setNotesFilter] = useState('');

  const [sortMode, setSortMode] = useState('OFFICE_PATIENT');

  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_COLUMNS);



  const [editingSuperbillId, setEditingSuperbillId] = useState(null);

  const [editForm, setEditForm] = useState({

    superbillDate: '',

    appointmentDate: '',

    officeId: '',

    doctorId: '',

    diagCombId: '',

    typeVisit: '',

    notes: '',

    billed: false,

    notesCompleted: false,

  });



  const [editingAuths, setEditingAuths] = useState([]);

  const [editingDiagOptions, setEditingDiagOptions] = useState([]);



  const superbillService = new SuperbillService();

  const authService = new PatientAuthorizationService();

  const diagService = new DiagnosisCombinerService();

  const officeService = new OfficeService();



  const role = localStorage.getItem('role')?.replace('ROLE_', '') || '';

  const canBulkUpdate = role === 'ADMIN' || role === 'MANAGER';
  const canAccessClinicalNotes = ['ADMIN', 'MANAGER', 'DOCTOR'].includes(role);
  const canConfigureClinicalProfile = ['ADMIN', 'MANAGER', 'DOCTOR'].includes(role);



  const getStaffId = () => {

    const id =

      localStorage.getItem('doctorId') || sessionStorage.getItem('doctorId');



    return id ? parseInt(id, 10) : null;

  };

  const loadActiveBodyParts = async () => {
    try {
      const bodyParts = await authorizationClinicalProfileService.getActiveBodyParts();
      setActiveBodyParts(Array.isArray(bodyParts) ? bodyParts : []);
    } catch (err) {
      console.error('Failed to load active clinical body parts:', err);
      setActiveBodyParts([]);
    }
  };

  const getBodyPartById = (bodyPartTemplateId) => {
    const id = Number(bodyPartTemplateId);

    return activeBodyParts.find(
      (bodyPart) => Number(bodyPart.bodyPartTemplateId) === id
    );
  };

  const getLateralityOptions = (bodyPartTemplateId) => {
    const bodyPart = getBodyPartById(bodyPartTemplateId);

    if (!bodyPart) {
      return [];
    }

    if (!bodyPart.supportsLaterality) {
      return [{ value: 'NOT_APPLICABLE', label: 'N/A' }];
    }

    const options = [
      { value: 'LEFT', label: 'Left' },
      { value: 'RIGHT', label: 'Right' },
    ];

    if (bodyPart.allowsBilateral) {
      options.push({ value: 'BILATERAL', label: 'Bilateral' });
    }

    return options;
  };

  const getDefaultLaterality = (bodyPartTemplateId) => {
    const bodyPart = getBodyPartById(bodyPartTemplateId);

    if (!bodyPart) {
      return '';
    }

    return bodyPart.supportsLaterality ? '' : 'NOT_APPLICABLE';
  };

  const isClinicalProfileConfigurationError = (err) => {
    const message = String(err?.message || '').toLowerCase();

    return (
      message.includes('clinical profile') ||
      message.includes('body part must be configured') ||
      message.includes('laterality must be configured') ||
      message.includes('treating/signing provider must be configured') ||
      message.includes('authorization clinical profile is inactive')
    );
  };

  const extractAuthorizationIdFromError = (err) => {
    const message = String(err?.message || '');
    const match = message.match(/Authorization\s+(\d+)/i);

    return match ? Number(match[1]) : null;
  };

  const openClinicalProviderSearch = () => {
    const popupUrl = `/search-popup?type=Doctor&inputTarget=superbillAuthorizationClinicalProfileProvider&url=${encodeURIComponent(
      apiBaseUrl + '/doctorinfo/search/all'
    )}`;

    window.open(popupUrl, 'popup', 'width=700,height=700');
  };

  const closeClinicalProfileModal = () => {
    if (clinicalProfileModal.saving) {
      return;
    }

    setClinicalProfileModal({ ...EMPTY_CLINICAL_PROFILE_MODAL });
  };

  const updateClinicalProfileModal = (key, value) => {
    setClinicalProfileModal((prev) => ({
      ...prev,
      [key]: value,
      error: '',
    }));
  };

  const handleClinicalProfileBodyPartChange = (bodyPartTemplateId) => {
    setClinicalProfileModal((prev) => ({
      ...prev,
      bodyPartTemplateId,
      laterality: getDefaultLaterality(bodyPartTemplateId),
      error: '',
    }));
  };

  const openClinicalProfileSetup = async ({
    authId,
    superbillId,
    patientName,
  }) => {
    const cleanAuthId = Number(authId);
    const cleanSuperbillId = Number(superbillId);

    if (!Number.isInteger(cleanAuthId) || cleanAuthId <= 0) {
      setError(
        'The clinical note needs authorization setup, but the authorization ID could not be determined.'
      );
      return;
    }

    let existingProfile = null;

    try {
      existingProfile = await authorizationClinicalProfileService.getByAuthorization(
        cleanAuthId
      );
    } catch (profileError) {
      const message = String(profileError?.message || '').toLowerCase();

      if (!message.includes('does not have a clinical profile')) {
        console.warn('Could not pre-load authorization clinical profile:', profileError);
      }
    }

    const bodyPartTemplateId = existingProfile?.bodyPartTemplateId || '';

    setClinicalProfileModal({
      open: true,
      authId: cleanAuthId,
      superbillId: cleanSuperbillId,
      patientName: patientName || '',
      bodyPartTemplateId,
      laterality:
        existingProfile?.laterality || getDefaultLaterality(bodyPartTemplateId),
      providerId: existingProfile?.providerId || '',
      providerName: existingProfile?.providerName || '',
      saving: false,
      error: '',
    });
  };



  const formatDisplayDate = (value) => {

    if (!value) return 'N/A';



    const parts = String(value).split('-');



    if (parts.length !== 3) {

      return value;

    }



    const [year, month, day] = parts;

    return `${month}-${day}-${year}`;

  };



  const formatText = (value) => {

    if (value === null || value === undefined || String(value).trim() === '') {

      return 'N/A';

    }



    return value;

  };



  const formatTypeVisit = (typeVisit) => {

    if (!typeVisit) return 'N/A';



    const normalized = String(typeVisit).trim().toLowerCase();



    if (normalized === 'progress note') return 'PN';

    if (normalized === 'discharge') return 'DIS';



    return typeVisit;

  };



  const shouldHideAuthorization = (row) => {

    return row?.hideAuthorizationInfo === true;

  };



  const getAuthorizationDisplay = (row) => {

    if (shouldHideAuthorization(row)) return '';

    return row?.authCode || '';

  };



  const getAuthDatesDisplay = (row) => {

    if (shouldHideAuthorization(row)) return '';



    const start = row?.authStartDate ? formatDisplayDate(row.authStartDate) : '';

    const end = row?.authEndDate ? formatDisplayDate(row.authEndDate) : '';



    if (!start && !end) return '';

    return `${start || 'N/A'} to ${end || 'N/A'}`;

  };



  const getTotalVisitsDisplay = (row) => {

    if (shouldHideAuthorization(row)) return '';

    return row?.totalVisits ?? '';

  };



  const getVisitsRemainingDisplay = (row) => {

    if (shouldHideAuthorization(row)) return '';

    return row?.visitsRemaining ?? '';

  };



  const getCurrentRange = () => {

    if (dateMode === 'single') {

      return {

        startDate: selectedDate,

        endDate: selectedDate,

      };

    }



    if (dateMode === 'range') {

      return {

        startDate,

        endDate,

      };

    }



    if (dateMode === 'startToToday') {

      return {

        startDate,

        endDate: today,

      };

    }



    if (dateMode === 'past7') {

      return {

        startDate: getPastDateString(7),

        endDate: today,

      };

    }



    if (dateMode === 'past30') {

      return {

        startDate: getPastDateString(30),

        endDate: today,

      };

    }



    return {

      startDate: today,

      endDate: today,

    };

  };



  const getDateLabel = () => {

    const range = getCurrentRange();



    return range.startDate === range.endDate

      ? formatDisplayDate(range.startDate)

      : `${formatDisplayDate(range.startDate)}-to-${formatDisplayDate(

          range.endDate

        )}`;

  };



  const normalizeBooleanFilter = (value) => {

    if (value === 'true') return true;

    if (value === 'false') return false;

    return null;

  };



  const buildBulkPayload = () => {

    const range = getCurrentRange();



    return {

      startDate: range.startDate,

      endDate: range.endDate,

      patientSearch: debouncedPatientSearch,

      insuranceSearch: debouncedInsuranceSearch,

      officeId: officeFilter ? parseInt(officeFilter, 10) : null,

      billed: normalizeBooleanFilter(billedFilter),

      notesCompleted: normalizeBooleanFilter(notesFilter),

      staffId: getStaffId(),

    };

  };



  useEffect(() => {
    const handleMessage = (event) => {
      const { inputTarget, value, displayName } = event.data || {};

      if (inputTarget === 'superbillAuthorizationClinicalProfileProvider') {
        setClinicalProfileModal((prev) => ({
          ...prev,
          providerId: value || '',
          providerName: displayName || (value ? `Doctor #${value}` : ''),
          error: '',
        }));
      }
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    loadActiveBodyParts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {

    const loadOffices = async () => {

      try {

        const officeList = await officeService.getAllOffices();

        setOffices(Array.isArray(officeList) ? officeList : []);

      } catch (err) {

        console.error('Could not load offices:', err);

      }

    };



    loadOffices();

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, []);



  useEffect(() => {

    const timer = setTimeout(() => {

      setDebouncedPatientSearch(patientSearch.trim());

    }, 500);



    return () => clearTimeout(timer);

  }, [patientSearch]);



  useEffect(() => {

    const timer = setTimeout(() => {

      setDebouncedInsuranceSearch(insuranceSearch.trim());

    }, 500);



    return () => clearTimeout(timer);

  }, [insuranceSearch]);



  useEffect(() => {

    setPageInfo((prev) => ({

      ...prev,

      page: 0,

    }));

  }, [

    dateMode,

    selectedDate,

    startDate,

    endDate,

    debouncedPatientSearch,

    debouncedInsuranceSearch,

    officeFilter,

    billedFilter,

    notesFilter,

    sortMode,

  ]);



  useEffect(() => {

    loadSuperbills();

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [

    dateMode,

    selectedDate,

    startDate,

    endDate,

    debouncedPatientSearch,

    debouncedInsuranceSearch,

    officeFilter,

    billedFilter,

    notesFilter,

    sortMode,

    pageInfo.page,

    pageInfo.size,

  ]);



  const loadSuperbills = async () => {

    setLoading(true);

    setError('');

    setSuccessMessage('');



    try {

      const range = getCurrentRange();



      if (range.startDate > range.endDate) {

        setError('Start date cannot be after end date.');

        setRows([]);

        return;

      }



      const result = await superbillService.getViewPaged({

        startDate: range.startDate,

        endDate: range.endDate,

        page: pageInfo.page,

        size: pageInfo.size,

        patientSearch: debouncedPatientSearch,

        insuranceSearch: debouncedInsuranceSearch,

        officeId: officeFilter || null,

        billed: normalizeBooleanFilter(billedFilter),

        notesCompleted: normalizeBooleanFilter(notesFilter),

        sortMode,

      });



      const content = Array.isArray(result?.content) ? result.content : [];



      setRows(content);



      setPageInfo((prev) => ({

        ...prev,

        page: result?.number ?? prev.page,

        size: result?.size ?? prev.size,

        totalPages: result?.totalPages ?? 1,

        totalElements: result?.totalElements ?? content.length,

      }));

    } catch (err) {

      console.error('Error loading superbills:', err);

      setError(err.message || 'Error loading superbills. Please try again.');

      setRows([]);

    } finally {

      setLoading(false);

    }

  };



  const handleBulkMarkBilled = async () => {

    if (!canBulkUpdate) {

      alert('Only ADMIN or MANAGER can bulk mark billed.');

      return;

    }



    const staffId = getStaffId();



    if (!staffId) {

      alert('No staff/doctorId found. Please log in again.');

      return;

    }



    const total = pageInfo.totalElements || rows.length;



    if (total <= 0) {

      alert('No superbills match the current filters.');

      return;

    }



    const confirmed = window.confirm(

      `This will mark ALL ${total} superbill(s) matching the current filters as billed. Continue?`

    );



    if (!confirmed) return;



    try {

      setBulkLoading(true);

      setError('');



      const response = await superbillService.bulkMarkBilled(buildBulkPayload());



      setSuccessMessage(

        response?.message ||

          `Bulk billed ${response?.updatedCount ?? 0} superbill(s).`

      );



      await loadSuperbills();

    } catch (err) {

      console.error('Bulk mark billed failed:', err);

      setError(err.message || 'Bulk mark billed failed.');

    } finally {

      setBulkLoading(false);

    }

  };



  const handleBulkMarkNotes = async () => {

    if (!canBulkUpdate) {

      alert('Only ADMIN or MANAGER can bulk mark notes complete.');

      return;

    }



    const staffId = getStaffId();



    if (!staffId) {

      alert('No staff/doctorId found. Please log in again.');

      return;

    }



    const total = pageInfo.totalElements || rows.length;



    if (total <= 0) {

      alert('No superbills match the current filters.');

      return;

    }



    const confirmed = window.confirm(

      `This will mark notes complete for ALL ${total} superbill(s) matching the current filters. Continue?`

    );



    if (!confirmed) return;



    try {

      setBulkLoading(true);

      setError('');



      const response = await superbillService.bulkMarkNotesCompleted(

        buildBulkPayload()

      );



      setSuccessMessage(

        response?.message ||

          `Bulk marked notes complete for ${

            response?.updatedCount ?? 0

          } superbill(s).`

      );



      await loadSuperbills();

    } catch (err) {

      console.error('Bulk mark notes failed:', err);

      setError(err.message || 'Bulk mark notes failed.');

    } finally {

      setBulkLoading(false);

    }

  };



  const fetchAllFilteredRows = async () => {

    const range = getCurrentRange();



    if (range.startDate > range.endDate) {

      throw new Error('Start date cannot be after end date.');

    }



    const exportSize = 500;

    let exportPage = 0;

    let totalPages = 1;

    let allRows = [];



    do {

      const result = await superbillService.getViewPaged({

        startDate: range.startDate,

        endDate: range.endDate,

        page: exportPage,

        size: exportSize,

        patientSearch: debouncedPatientSearch,

        insuranceSearch: debouncedInsuranceSearch,

        officeId: officeFilter || null,

        billed: normalizeBooleanFilter(billedFilter),

        notesCompleted: normalizeBooleanFilter(notesFilter),

        sortMode,

      });



      const content = Array.isArray(result?.content) ? result.content : [];



      allRows = [...allRows, ...content];



      totalPages = result?.totalPages || 1;

      exportPage += 1;

    } while (exportPage < totalPages);



    return allRows;

  };



  const toggleExpand = (superbillId) => {

    setExpanded((prev) => ({

      ...prev,

      [superbillId]: !prev[superbillId],

    }));

  };



  const handleMarkBilled = async (superbillId) => {

    const staffId = getStaffId();



    if (!staffId) {

      alert('No staff/doctorId found. Please log in again.');

      return;

    }



    try {

      await superbillService.markBilled(superbillId, staffId);

      setSuccessMessage('Superbill marked as billed.');

      setTimeout(() => setSuccessMessage(''), 3000);

      await loadSuperbills();

    } catch (err) {

      console.error('Error marking billed:', err);

      setError(err.message || 'Error marking billed. Please try again.');

    }

  };



  const handleMarkNotes = async (superbillId) => {

    const staffId = getStaffId();



    if (!staffId) {

      alert('No staff/doctorId found. Please log in again.');

      return;

    }



    try {

      await superbillService.markNotesCompleted(superbillId, staffId);

      setSuccessMessage('Notes marked as completed.');

      setTimeout(() => setSuccessMessage(''), 3000);

      await loadSuperbills();

    } catch (err) {

      console.error('Error marking notes complete:', err);

      setError(err.message || 'Error marking notes complete. Please try again.');

    }

  };



  const getClinicalNoteId = (note) => {
    return (
      note?.clinicalNoteId ??
      note?.note?.clinicalNoteId ??
      note?.clinicalNote?.clinicalNoteId ??
      null
    );
  };

  const handleClinicalNote = async (row) => {
    if (!canAccessClinicalNotes) {
      alert('You do not have permission to open clinical notes.');
      return;
    }

    const superbillId = Number(row?.superbillId);

    if (!Number.isInteger(superbillId) || superbillId <= 0) {
      alert('A valid superbill ID is required to open a clinical note.');
      return;
    }

    if (clinicalNoteLoadingBySuperbill[superbillId]) {
      return;
    }

    setClinicalNoteLoadingBySuperbill((prev) => ({
      ...prev,
      [superbillId]: true,
    }));

    setError('');

    try {
      let note = null;
      const exists = await clinicalNoteService.existsForSuperbill(superbillId);

      if (exists) {
        note = await clinicalNoteService.getClinicalNoteBySuperbill(superbillId);
      } else {
        const confirmed = window.confirm(
          `No clinical note exists for superbill #${superbillId}. Create a Draft clinical note now?`
        );

        if (!confirmed) {
          return;
        }

        try {
          note = await clinicalNoteService.createClinicalNote({ superbillId });
        } catch (createError) {
          /*
           * Another staff member may have created the note between the
           * existence check and this POST. Recover by loading that note.
           */
          const nowExists = await clinicalNoteService
            .existsForSuperbill(superbillId)
            .catch(() => false);

          if (nowExists) {
            note = await clinicalNoteService.getClinicalNoteBySuperbill(
              superbillId
            );
          } else if (isClinicalProfileConfigurationError(createError)) {
            const authId =
              Number(row?.authId) || extractAuthorizationIdFromError(createError);

            await openClinicalProfileSetup({
              authId,
              superbillId,
              patientName: row?.patientName || '',
            });
            return;
          } else {
            throw createError;
          }
        }
      }

      const clinicalNoteId = getClinicalNoteId(note);

      if (!clinicalNoteId) {
        throw new Error('The clinical note was found, but its ID was missing.');
      }

      window.location.href = `/clinical-notes/${clinicalNoteId}`;
    } catch (err) {
      console.error('Clinical note open/create failed:', err);
      setError(err.message || 'Clinical note could not be opened.');
    } finally {
      setClinicalNoteLoadingBySuperbill((prev) => {
        const next = { ...prev };
        delete next[superbillId];
        return next;
      });
    }
  };

  const saveClinicalProfileAndOpenNote = async () => {
    if (!canConfigureClinicalProfile) {
      updateClinicalProfileModal(
        'error',
        'You do not have permission to configure the authorization clinical profile.'
      );
      return;
    }

    const authId = Number(clinicalProfileModal.authId);
    const superbillId = Number(clinicalProfileModal.superbillId);
    const bodyPartTemplateId = Number(clinicalProfileModal.bodyPartTemplateId);
    const providerId = Number(clinicalProfileModal.providerId);
    const staffId = Number(getStaffId());

    if (!Number.isInteger(authId) || authId <= 0) {
      updateClinicalProfileModal('error', 'A valid authorization ID is required.');
      return;
    }

    if (!Number.isInteger(bodyPartTemplateId) || bodyPartTemplateId <= 0) {
      updateClinicalProfileModal('error', 'Please select a body part.');
      return;
    }

    if (!clinicalProfileModal.laterality) {
      updateClinicalProfileModal('error', 'Please select laterality.');
      return;
    }

    if (!Number.isInteger(providerId) || providerId <= 0) {
      updateClinicalProfileModal(
        'error',
        'Please select the treating/signing provider.'
      );
      return;
    }

    if (!Number.isInteger(staffId) || staffId <= 0) {
      updateClinicalProfileModal(
        'error',
        'No staff/doctor ID was found. Please log in again.'
      );
      return;
    }

    if (!Number.isInteger(superbillId) || superbillId <= 0) {
      updateClinicalProfileModal('error', 'A valid superbill ID is required.');
      return;
    }

    setClinicalProfileModal((prev) => ({
      ...prev,
      saving: true,
      error: '',
    }));

    try {
      await authorizationClinicalProfileService.saveForAuthorization(authId, {
        bodyPartTemplateId,
        laterality: clinicalProfileModal.laterality,
        providerId,
        active: true,
        staffId,
      });

      let note = null;

      try {
        note = await clinicalNoteService.createClinicalNote({ superbillId });
      } catch (createError) {
        const nowExists = await clinicalNoteService
          .existsForSuperbill(superbillId)
          .catch(() => false);

        if (!nowExists) {
          throw createError;
        }

        note = await clinicalNoteService.getClinicalNoteBySuperbill(superbillId);
      }

      const clinicalNoteId = getClinicalNoteId(note);

      if (!clinicalNoteId) {
        throw new Error('The clinical note was created, but its ID was missing.');
      }

      window.location.href = `/clinical-notes/${clinicalNoteId}`;
    } catch (err) {
      console.error('Clinical profile save / note creation failed:', err);
      setClinicalProfileModal((prev) => ({
        ...prev,
        saving: false,
        error: err.message || 'Clinical profile could not be saved.',
      }));
    }
  };

  const startEdit = async (row) => {

    setEditingSuperbillId(row.superbillId);



    setEditForm({

      superbillDate: row.superbillDate || '',

      appointmentDate: row.appointmentDate || row.superbillDate || '',

      officeId: row.officeId || '',

      doctorId: row.doctorId || '',

      diagCombId: row.diagCombId || '',

      typeVisit: row.typeVisit || '',

      notes: row.appointmentNotes || '',

      billed: !!row.billed,

      notesCompleted: !!row.notesCompleted,

    });



    setEditingAuths([]);

    setEditingDiagOptions([]);



    if (row.patientId) {

      try {

        const auths = await authService.findByPatientId(row.patientId);

        setEditingAuths(Array.isArray(auths) ? auths : []);

      } catch (err) {

        console.error('Could not load authorizations for edit:', err);

        setEditingAuths([]);

      }

    }



    if (row.authId) {

      try {

        const diagRecords = await diagService.getByAuthId(row.authId);

        setEditingDiagOptions(Array.isArray(diagRecords) ? diagRecords : []);

      } catch (err) {

        console.error('Could not load diagnosis records for edit:', err);

        setEditingDiagOptions([]);

      }

    }

  };



  const cancelEdit = () => {

    setEditingSuperbillId(null);



    setEditForm({

      superbillDate: '',

      appointmentDate: '',

      officeId: '',

      doctorId: '',

      diagCombId: '',

      typeVisit: '',

      notes: '',

      billed: false,

      notesCompleted: false,

    });



    setEditingAuths([]);

    setEditingDiagOptions([]);

  };



  const handleEditChange = async (key, value) => {

    setEditForm((prev) => ({

      ...prev,

      [key]: value,

    }));



    if (key === 'authId') {

      try {

        const diagRecords = await diagService.getByAuthId(value);

        const list = Array.isArray(diagRecords) ? diagRecords : [];



        setEditingDiagOptions(list);



        if (list.length > 0) {

          setEditForm((prev) => ({

            ...prev,

            diagCombId: list[0].diagCombId,

          }));

        }

      } catch (err) {

        console.error('Could not load diagnosis records:', err);

        setEditingDiagOptions([]);

      }

    }

  };



  const saveEdit = async (row) => {

    const staffId = getStaffId();



    const payload = {

      superbillDate: editForm.superbillDate,

      appointmentDate: editForm.appointmentDate,

      officeId: editForm.officeId ? parseInt(editForm.officeId, 10) : null,

      doctorId: editForm.doctorId ? parseInt(editForm.doctorId, 10) : null,

      diagCombId: editForm.diagCombId

        ? parseInt(editForm.diagCombId, 10)

        : null,

      typeVisit: editForm.typeVisit,

      notes: editForm.notes,

      billed: editForm.billed,

      notesCompleted: editForm.notesCompleted,

      billedByStaffId: editForm.billed ? staffId : null,

      notesCompletedByStaffId: editForm.notesCompleted ? staffId : null,

    };



    try {

      await superbillService.updateSuperbill(row.superbillId, payload);

      setSuccessMessage('Superbill updated successfully.');

      setTimeout(() => setSuccessMessage(''), 3000);

      cancelEdit();

      await loadSuperbills();

    } catch (err) {

      console.error('Error updating superbill:', err);

      alert(err.message || 'Failed to update superbill.');

    }

  };



  const deleteSuperbill = async (row) => {

    const confirmed = window.confirm(

      `Delete superbill for ${row.patientName}? This will restore 1 visit to the connected authorization.`

    );



    if (!confirmed) return;



    try {

      await superbillService.deleteSuperbill(row.superbillId);

      setSuccessMessage('Superbill deleted and authorization visit restored.');

      setTimeout(() => setSuccessMessage(''), 3000);



      if (rows.length === 1 && pageInfo.page > 0) {

        setPageInfo((prev) => ({

          ...prev,

          page: prev.page - 1,

        }));

      } else {

        await loadSuperbills();

      }

    } catch (err) {

      console.error('Error deleting superbill:', err);

      alert(err.message || 'Failed to delete superbill.');

    }

  };



  const toggleColumn = (key) => {

    setVisibleColumns((prev) => ({

      ...prev,

      [key]: !prev[key],

    }));

  };



  const csvEscape = (value) => {

    const str = String(value ?? '');

    return `"${str.replace(/"/g, '""')}"`;

  };



  const getExportColumns = () => {

    return Object.entries(visibleColumns)

      .filter(([key, visible]) => visible && key !== 'actions')

      .map(([key]) => key);

  };



  const getColumnLabels = () => ({

    date: 'Date',

    patient: 'Patient',

    dob: 'DOB',

    insurance: 'Insurance',

    doctor: 'Doctor',

    office: 'Office',

    authorization: 'Authorization',

    authDates: 'Authorization Dates',

    totalVisits: 'Total Visits',

    visitsRemaining: 'Visits Remaining',

    typeVisit: 'Type Visit',

    procedure: 'Procedure',

    diagnosis: 'Diagnosis',

    billed: 'Billed',

    notesCompleted: 'Notes Completed',

  });



  const getRowValue = (row, key) => {

    switch (key) {

      case 'date':

        return formatDisplayDate(row.superbillDate);

      case 'patient':

        return row.patientName || '';

      case 'dob':

        return formatDisplayDate(row.dob);

      case 'insurance':

        return row.insuranceName || '';

      case 'doctor':

        return row.doctorName || '';

      case 'office':

        return row.officeName || '';

      case 'authorization':

        return getAuthorizationDisplay(row);

      case 'authDates':

        return getAuthDatesDisplay(row);

      case 'totalVisits':

        return getTotalVisitsDisplay(row);

      case 'visitsRemaining':

        return getVisitsRemainingDisplay(row);

      case 'typeVisit':

        return formatTypeVisit(row.typeVisit);

      case 'procedure':

        return row.procedure || '';

      case 'diagnosis':

        return row.diagnosis || '';

      case 'billed':

        return row.billed ? 'X' : '';

      case 'notesCompleted':

        return row.notesCompleted ? 'X' : '';

      default:

        return '';

    }

  };



  const exportCsv = async () => {

    try {

      setLoading(true);

      setError('');



      const allExportRows = await fetchAllFilteredRows();



      if (allExportRows.length === 0) {

        alert('No superbills found to export.');

        return;

      }



      const exportColumns = getExportColumns();

      const labels = getColumnLabels();



      const header = exportColumns

        .map((column) => csvEscape(labels[column] || column))

        .join(',');



      const lines = allExportRows.map((row) =>

        exportColumns

          .map((column) => csvEscape(getRowValue(row, column)))

          .join(',')

      );



      const csv = [header, ...lines].join('\n');



      const blob = new Blob([csv], {

        type: 'text/csv;charset=utf-8;',

      });



      const url = URL.createObjectURL(blob);



      const link = document.createElement('a');

      link.href = url;

      link.download = `superbills-${getDateLabel()}-all-results.csv`;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);



      URL.revokeObjectURL(url);



      setSuccessMessage(`Exported ${allExportRows.length} superbill(s).`);

      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (err) {

      console.error('Error exporting superbills:', err);

      setError(err.message || 'Could not export superbills.');

    } finally {

      setLoading(false);

    }

  };



  const handlePrint = async () => {

    try {

      setLoading(true);

      setError('');



      const printRows = await fetchAllFilteredRows();



      if (printRows.length === 0) {

        alert('No superbills found to print.');

        return;

      }



      const printableColumns = getExportColumns();

      const labels = getColumnLabels();



      const escapeHtml = (value) => {

        return String(value ?? '')

          .replaceAll('&', '&amp;')

          .replaceAll('<', '&lt;')

          .replaceAll('>', '&gt;')

          .replaceAll('"', '&quot;')

          .replaceAll("'", '&#039;');

      };



      const tableHeaders = printableColumns

        .map((column) => `<th>${escapeHtml(labels[column] || column)}</th>`)

        .join('');



      const tableRows = printRows

        .map((row) => {

          const cells = printableColumns

            .map(

              (column) =>

                `<td>${escapeHtml(getRowValue(row, column))}</td>`

            )

            .join('');



          return `<tr>${cells}</tr>`;

        })

        .join('');



      const printWindow = window.open('', '_blank');



      if (!printWindow) {

        alert('Popup blocked. Please allow popups to print.');

        return;

      }



      printWindow.document.write(`

        <!DOCTYPE html>

        <html>

          <head>

            <title>Superbills ${escapeHtml(getDateLabel())}</title>

            <style>

              body {

                font-family: Arial, sans-serif;

                padding: 20px;

                color: #111827;

              }



              h1 {

                text-align: center;

                margin-bottom: 8px;

              }



              .summary {

                text-align: center;

                margin-bottom: 18px;

                font-size: 14px;

              }



              table {

                width: 100%;

                border-collapse: collapse;

                font-size: 10px;

              }



              th {

                background: #e5e7eb;

                font-weight: bold;

              }



              th, td {

                border: 1px solid #9ca3af;

                padding: 5px;

                text-align: left;

                vertical-align: top;

              }



              td {

                word-break: break-word;

              }



              @media print {

                body {

                  padding: 0;

                }



                table {

                  font-size: 8px;

                }



                th, td {

                  padding: 4px;

                }

              }

            </style>

          </head>

          <body>

            <h1>Superbill Report</h1>

            <div class="summary">

              Date: ${escapeHtml(getDateLabel())} |

              Total: ${printRows.length}

            </div>



            <table>

              <thead>

                <tr>${tableHeaders}</tr>

              </thead>

              <tbody>

                ${tableRows}

              </tbody>

            </table>

          </body>

        </html>

      `);



      printWindow.document.close();

      printWindow.focus();



      setTimeout(() => {

        printWindow.print();

      }, 500);

    } catch (err) {

      console.error('Error printing superbills:', err);

      setError(err.message || 'Could not print superbills.');

    } finally {

      setLoading(false);

    }

  };



  const renderDetails = (row) => {

    return (

      <div className="sb-details-inner">

        <div className="sb-detail-row">

          <span className="sb-detail-label">Superbill ID:</span>

          <span>{row.superbillId}</span>

        </div>



        <div className="sb-detail-row">

          <span className="sb-detail-label">Appointment ID:</span>

          <span>{row.appointmentId}</span>

        </div>



        <div className="sb-detail-row">

          <span className="sb-detail-label">Appointment Date:</span>

          <span>{formatDisplayDate(row.appointmentDate)}</span>

        </div>



        <div className="sb-detail-row">

          <span className="sb-detail-label">Type of Visit:</span>

          <span>{formatTypeVisit(row.typeVisit)}</span>

        </div>



        {!shouldHideAuthorization(row) && (

          <>

            <div className="sb-detail-row">

              <span className="sb-detail-label">Authorization Code:</span>

              <span>{formatText(row.authCode)}</span>

            </div>



            <div className="sb-detail-row">

              <span className="sb-detail-label">Auth Range:</span>

              <span>{getAuthDatesDisplay(row) || 'N/A'}</span>

            </div>



            <div className="sb-detail-row">

              <span className="sb-detail-label">Total Visits:</span>

              <span>{row.totalVisits ?? 'N/A'}</span>

            </div>



            <div className="sb-detail-row">

              <span className="sb-detail-label">Visits Remaining:</span>

              <span>{row.visitsRemaining ?? 'N/A'}</span>

            </div>



            <div className="sb-detail-row">

              <span className="sb-detail-label">Auth Active:</span>

              <span>{formatText(row.authActive)}</span>

            </div>

          </>

        )}



        <div className="sb-detail-row">

          <span className="sb-detail-label">Subscriber ID:</span>

          <span>{formatText(row.subscriberId)}</span>

        </div>



        <div className="sb-detail-row">

          <span className="sb-detail-label">Billed By:</span>

          <span>{formatText(row.billedByName)}</span>

        </div>



        <div className="sb-detail-row">

          <span className="sb-detail-label">Notes Completed By:</span>

          <span>{formatText(row.notesCompletedByName)}</span>

        </div>



        <div className="sb-detail-row">

          <span className="sb-detail-label">Appointment Notes:</span>

          <span>{formatText(row.appointmentNotes)}</span>

        </div>

      </div>

    );

  };



  const renderEditPanel = (row) => {

    return (

      <div className="superbill-edit-panel">

        <h3>Edit Superbill</h3>



        <p className="superbill-edit-note">

          Patient, DOB, and insurance are read-only. If authorization is changed,

          the backend restores 1 visit to the old authorization and subtracts 1

          from the new authorization.

        </p>



        <div className="superbill-edit-grid">

          <label>

            Superbill Date

            <input

              type="date"

              value={editForm.superbillDate}

              onChange={(e) => handleEditChange('superbillDate', e.target.value)}

            />

          </label>



          <label>

            Appointment Date

            <input

              type="date"

              value={editForm.appointmentDate}

              onChange={(e) =>

                handleEditChange('appointmentDate', e.target.value)

              }

            />

          </label>



          <label>

            Office ID

            <input

              type="number"

              value={editForm.officeId}

              onChange={(e) => handleEditChange('officeId', e.target.value)}

            />

          </label>



          <label>

            Doctor ID

            <input

              type="number"

              value={editForm.doctorId}

              onChange={(e) => handleEditChange('doctorId', e.target.value)}

            />

          </label>



          <label>

            Authorization

            <select

              value=""

              onChange={(e) => handleEditChange('authId', e.target.value)}

            >

              <option value="">Change authorization...</option>

              {editingAuths.map((auth) => (

                <option key={auth.authId} value={auth.authId}>

                  {auth.authCode || `Auth #${auth.authId}`} | Total:{' '}

                  {auth.totalVisits ?? 'N/A'} | Remaining: {auth.visits} |

                  Active: {auth.active}

                </option>

              ))}

            </select>

          </label>



          <label>

            Diagnosis/Procedure Record

            <select

              value={editForm.diagCombId}

              onChange={(e) => handleEditChange('diagCombId', e.target.value)}

            >

              <option value={editForm.diagCombId}>

                Current: {editForm.diagCombId || 'N/A'}

              </option>



              {editingDiagOptions.map((diag) => (

                <option key={diag.diagCombId} value={diag.diagCombId}>

                  {diag.diagCombId} | Dx: {diag.diagnosisSet || 'None'} | Proc:{' '}

                  {diag.procedureSet || 'None'}

                </option>

              ))}

            </select>

          </label>



          <label>

            Type Visit

            <select

              value={editForm.typeVisit}

              onChange={(e) => handleEditChange('typeVisit', e.target.value)}

            >

              <option value="">Select</option>

              {TYPE_VISIT_OPTIONS.map((option) => (

                <option key={option} value={option}>

                  {formatTypeVisit(option)}

                </option>

              ))}

            </select>

          </label>



          <label>

            Notes

            <textarea

              value={editForm.notes}

              onChange={(e) => handleEditChange('notes', e.target.value)}

            />

          </label>



          <label className="sb-checkbox-label">

            <input

              type="checkbox"

              checked={editForm.billed}

              onChange={(e) => handleEditChange('billed', e.target.checked)}

            />

            Billed

          </label>



          <label className="sb-checkbox-label">

            <input

              type="checkbox"

              checked={editForm.notesCompleted}

              onChange={(e) =>

                handleEditChange('notesCompleted', e.target.checked)

              }

            />

            Notes Completed

          </label>

        </div>



        <div className="superbill-actions">

          <button className="sb-btn sb-btn-billed" onClick={() => saveEdit(row)}>

            Save Edit

          </button>



          <button className="sb-btn sb-btn-toggle" onClick={cancelEdit}>

            Cancel Edit

          </button>

        </div>

      </div>

    );

  };



  const renderActions = (row) => {

    return (

      <div className="sb-row-actions">

        <button

          className="sb-btn sb-btn-billed"

          disabled={row.billed}

          onClick={() => handleMarkBilled(row.superbillId)}

        >

          {row.billed ? 'Billed' : 'Bill'}

        </button>



        <button

          className="sb-btn sb-btn-notes"

          disabled={row.notesCompleted}

          onClick={() => handleMarkNotes(row.superbillId)}

        >

          {row.notesCompleted ? 'Notes Done' : 'Notes'}

        </button>



        {canAccessClinicalNotes && (
          <button
            type="button"
            className="sb-btn sb-btn-toggle"
            disabled={!!clinicalNoteLoadingBySuperbill[row.superbillId]}
            onClick={() => handleClinicalNote(row)}
          >
            {clinicalNoteLoadingBySuperbill[row.superbillId]
              ? 'Opening Note...'
              : 'Clinical Note'}
          </button>
        )}

        <button className="sb-btn sb-btn-toggle" onClick={() => startEdit(row)}>

          Edit

        </button>



        <button

          className="sb-btn sb-btn-delete"

          onClick={() => deleteSuperbill(row)}

        >

          Delete

        </button>

      </div>

    );

  };



  const renderClinicalProfileModal = () => {
    if (!clinicalProfileModal.open) {
      return null;
    }

    const lateralityOptions = getLateralityOptions(
      clinicalProfileModal.bodyPartTemplateId
    );

    return (
      <div
        className="sb-clinical-profile-backdrop"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sb-clinical-profile-title"
      >
        <div className="sb-clinical-profile-modal">
          <div className="sb-clinical-profile-header">
            <div>
              <h2 id="sb-clinical-profile-title">Authorization Clinical Profile</h2>
              <p>
                Authorization #{clinicalProfileModal.authId}
                {clinicalProfileModal.patientName
                  ? ` | ${clinicalProfileModal.patientName}`
                  : ''}
              </p>
            </div>

            <button
              type="button"
              className="sb-clinical-profile-close"
              onClick={closeClinicalProfileModal}
              disabled={clinicalProfileModal.saving}
            >
              Close
            </button>
          </div>

          <div className="sb-clinical-profile-content">
            <p className="sb-clinical-profile-help">
              This authorization must be configured before its clinical note can
              be created. The selected body part, laterality, and provider become
              the authorization-level clinical configuration used by its notes.
            </p>

            {clinicalProfileModal.error && (
              <div className="sb-clinical-profile-error">
                {clinicalProfileModal.error}
              </div>
            )}

            <div className="sb-clinical-profile-grid">
              <label>
                <span>Body Part</span>
                <select
                  value={clinicalProfileModal.bodyPartTemplateId || ''}
                  onChange={(e) =>
                    handleClinicalProfileBodyPartChange(e.target.value)
                  }
                  disabled={clinicalProfileModal.saving}
                >
                  <option value="">Select body part</option>
                  {activeBodyParts.map((bodyPart) => (
                    <option
                      key={bodyPart.bodyPartTemplateId}
                      value={bodyPart.bodyPartTemplateId}
                    >
                      {bodyPart.displayName ||
                        bodyPart.bodyPartCode ||
                        `Body Part #${bodyPart.bodyPartTemplateId}`}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span>Laterality</span>
                <select
                  value={clinicalProfileModal.laterality || ''}
                  onChange={(e) =>
                    updateClinicalProfileModal('laterality', e.target.value)
                  }
                  disabled={
                    clinicalProfileModal.saving ||
                    !clinicalProfileModal.bodyPartTemplateId
                  }
                >
                  <option value="">Select laterality</option>
                  {lateralityOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <div className="sb-clinical-profile-provider">
                <span className="sb-clinical-profile-label">
                  Treating / Signing Provider
                </span>

                <button
                  type="button"
                  className="sb-btn sb-btn-toggle"
                  onClick={openClinicalProviderSearch}
                  disabled={clinicalProfileModal.saving}
                >
                  Select Provider
                </button>

                <input
                  type="text"
                  value={clinicalProfileModal.providerName || ''}
                  readOnly
                  placeholder={
                    clinicalProfileModal.providerId
                      ? `Doctor #${clinicalProfileModal.providerId}`
                      : 'Selected provider'
                  }
                />

                <small>
                  Provider ID: {clinicalProfileModal.providerId || 'N/A'}
                </small>
              </div>
            </div>

            <div className="sb-clinical-profile-actions">
              <button
                type="button"
                className="sb-btn sb-btn-billed"
                onClick={saveClinicalProfileAndOpenNote}
                disabled={clinicalProfileModal.saving}
              >
                {clinicalProfileModal.saving
                  ? 'Saving + Opening...'
                  : 'Save & Open Clinical Note'}
              </button>

              <button
                type="button"
                className="sb-btn sb-btn-toggle"
                onClick={closeClinicalProfileModal}
                disabled={clinicalProfileModal.saving}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const goToPreviousPage = () => {

    setPageInfo((prev) => ({

      ...prev,

      page: Math.max(prev.page - 1, 0),

    }));

  };



  const goToNextPage = () => {

    setPageInfo((prev) => ({

      ...prev,

      page:

        prev.totalPages && prev.page + 1 < prev.totalPages

          ? prev.page + 1

          : prev.page,

    }));

  };



  const changePageSize = (size) => {

    setPageInfo((prev) => ({

      ...prev,

      page: 0,

      size: parseInt(size, 10),

    }));

  };



  return (

    <div className="superbill-container">

      {(loading || bulkLoading) && (

        <div className="superbill-loading-overlay">

          <div className="superbill-loading-content">

            <div className="superbill-spinner"></div>

            <div>

              {bulkLoading

                ? 'Applying bulk update to filtered superbills...'

                : 'Loading superbills...'}

            </div>

          </div>

        </div>

      )}



      <h1>Superbill Management</h1>



      {error && <div className="superbill-error">{error}</div>}

      {successMessage && (

        <div className="superbill-success">{successMessage}</div>

      )}



      <div className="superbill-toolbar">

        <div className="superbill-view-buttons">

          <button

            type="button"

            className={viewMode === 'cards' ? 'active' : ''}

            onClick={() => setViewMode('cards')}

          >

            Card View

          </button>



          <button

            type="button"

            className={viewMode === 'grid' ? 'active' : ''}

            onClick={() => setViewMode('grid')}

          >

            Grid View

          </button>

        </div>



        <div className="superbill-export-buttons">

          <button type="button" onClick={exportCsv}>

            Export All Results CSV

          </button>



          <button type="button" onClick={handlePrint}>

            Print / Save PDF

          </button>

        </div>

      </div>



      {canBulkUpdate && (

        <div

          className="superbill-bulk-actions"

          style={{

            display: 'flex',

            gap: '10px',

            flexWrap: 'wrap',

            alignItems: 'center',

            marginBottom: '12px',

            padding: '12px',

            border: '1px solid #f59e0b',

            borderRadius: '12px',

            background: '#fffbeb',

          }}

        >

          <strong>Bulk Actions:</strong>



          <button

            type="button"

            onClick={handleBulkMarkBilled}

            disabled={bulkLoading || pageInfo.totalElements === 0}

          >

            Mark All Filtered Billed

          </button>



          <button

            type="button"

            onClick={handleBulkMarkNotes}

            disabled={bulkLoading || pageInfo.totalElements === 0}

          >

            Mark All Filtered Notes Complete

          </button>



          <span style={{ fontSize: '0.9rem', color: '#92400e' }}>

            Applies to all <strong>{pageInfo.totalElements}</strong> matching

            result(s), not just this page.

          </span>

        </div>

      )}



      <div className="superbill-controls">

        <label>

          Date Mode:

          <select value={dateMode} onChange={(e) => setDateMode(e.target.value)}>

            <option value="single">Single Date</option>

            <option value="range">Date Range</option>

            <option value="startToToday">Start Date to Today</option>

            <option value="past7">Past 7 Days</option>

            <option value="past30">Past 30 Days</option>

          </select>

        </label>



        {dateMode === 'single' && (

          <label>

            Date:

            <input

              type="date"

              value={selectedDate}

              onChange={(e) => setSelectedDate(e.target.value)}

            />

          </label>

        )}



        {dateMode === 'range' && (

          <>

            <label>

              Start Date:

              <input

                type="date"

                value={startDate}

                onChange={(e) => setStartDate(e.target.value)}

              />

            </label>



            <label>

              End Date:

              <input

                type="date"

                value={endDate}

                onChange={(e) => setEndDate(e.target.value)}

              />

            </label>

          </>

        )}



        {dateMode === 'startToToday' && (

          <label>

            Start Date:

            <input

              type="date"

              value={startDate}

              onChange={(e) => setStartDate(e.target.value)}

            />

          </label>

        )}



        <label>

          Page Size:

          <select

            value={pageInfo.size}

            onChange={(e) => changePageSize(e.target.value)}

          >

            <option value="20">20</option>

            <option value="25">25</option>

            <option value="50">50</option>

          </select>

        </label>



        <label>

          Office:

          <select

            value={officeFilter}

            onChange={(e) => setOfficeFilter(e.target.value)}

          >

            <option value="">All Offices</option>

            {offices.map((office) => (

              <option key={office.officeId} value={office.officeId}>

                {office.officeName}

              </option>

            ))}

          </select>

        </label>



        <label>

          Patient:

          <input

            type="text"

            value={patientSearch}

            onChange={(e) => setPatientSearch(e.target.value)}

            placeholder="Search patient"

          />

        </label>



        <label>

          Insurance:

          <input

            type="text"

            value={insuranceSearch}

            onChange={(e) => setInsuranceSearch(e.target.value)}

            placeholder="Search insurance or subscriber ID"

          />

        </label>



        <label>

          Billing:

          <select

            value={billedFilter}

            onChange={(e) => setBilledFilter(e.target.value)}

          >

            <option value="">All</option>

            <option value="false">Unbilled</option>

            <option value="true">Billed</option>

          </select>

        </label>



        <label>

          Notes:

          <select

            value={notesFilter}

            onChange={(e) => setNotesFilter(e.target.value)}

          >

            <option value="">All</option>

            <option value="false">Notes Not Done</option>

            <option value="true">Notes Done</option>

          </select>

        </label>



        <label>

          Sort:

          <select value={sortMode} onChange={(e) => setSortMode(e.target.value)}>

            <option value="OFFICE_PATIENT">Office + Patient A-Z</option>

            <option value="PATIENT">Patient A-Z</option>

            <option value="MOST_RECENT">Most Recent</option>

            <option value="NEEDS_WORK">Needs Billing/Notes First</option>

          </select>

        </label>

      </div>



      {viewMode === 'grid' && (

        <div className="superbill-column-controls">

          {Object.entries(DEFAULT_COLUMNS).map(([key]) => (

            <label key={key}>

              <input

                type="checkbox"

                checked={!!visibleColumns[key]}

                onChange={() => toggleColumn(key)}

              />

              {key}

            </label>

          ))}

        </div>

      )}



      <div className="superbill-pagination">

        <button

          type="button"

          disabled={pageInfo.page <= 0}

          onClick={goToPreviousPage}

        >

          Previous

        </button>



        <span>

          Page <strong>{pageInfo.page + 1}</strong> of{' '}

          <strong>{pageInfo.totalPages || 1}</strong> | Total:{' '}

          <strong>{pageInfo.totalElements}</strong>

        </span>



        <button

          type="button"

          disabled={

            !pageInfo.totalPages || pageInfo.page + 1 >= pageInfo.totalPages

          }

          onClick={goToNextPage}

        >

          Next

        </button>

      </div>



      <div className="superbill-summary">

        Showing <strong>{rows.length}</strong> superbill(s) on this page

      </div>



      {viewMode === 'cards' ? (

        <div className="superbill-list">

          {rows.length === 0 && !loading && (

            <div className="superbill-empty">No superbills match this view.</div>

          )}



          {rows.map((row) => {

            const isEditing = editingSuperbillId === row.superbillId;



            return (

              <div className="superbill-card" key={row.superbillId}>

                <div className="superbill-main">

                  <div className="sb-main-row">

                    <span className="sb-label">Superbill Date:</span>

                    <span>{formatDisplayDate(row.superbillDate)}</span>

                  </div>



                  <div className="sb-main-row">

                    <span className="sb-label">Patient:</span>

                    <span>{formatText(row.patientName)}</span>

                  </div>



                  <div className="sb-main-row">

                    <span className="sb-label">DOB:</span>

                    <span>{formatDisplayDate(row.dob)}</span>

                  </div>



                  <div className="sb-main-row">

                    <span className="sb-label">Insurance:</span>

                    <span>{formatText(row.insuranceName)}</span>

                  </div>



                  <div className="sb-main-row">

                    <span className="sb-label">Doctor:</span>

                    <span>{formatText(row.doctorName)}</span>

                  </div>



                  <div className="sb-main-row">

                    <span className="sb-label">Office:</span>

                    <span>{formatText(row.officeName)}</span>

                  </div>



                  <div className="sb-main-row">

                    <span className="sb-label">Type Visit:</span>

                    <span>{formatTypeVisit(row.typeVisit)}</span>

                  </div>



                  {!shouldHideAuthorization(row) && (

                    <>

                      <div className="sb-main-row">

                        <span className="sb-label">Authorization:</span>

                        <span>{formatText(row.authCode)}</span>

                      </div>



                      <div className="sb-main-row">

                        <span className="sb-label">Auth Dates:</span>

                        <span>{getAuthDatesDisplay(row) || 'N/A'}</span>

                      </div>



                      <div className="sb-main-row">

                        <span className="sb-label">Total Visits:</span>

                        <span>{row.totalVisits ?? 'N/A'}</span>

                      </div>



                      <div className="sb-main-row">

                        <span className="sb-label">Remaining Visits:</span>

                        <span>{row.visitsRemaining ?? 'N/A'}</span>

                      </div>

                    </>

                  )}



                  <div className="sb-main-row">

                    <span className="sb-label">Procedure:</span>

                    <span>{formatText(row.procedure)}</span>

                  </div>



                  <div className="sb-main-row">

                    <span className="sb-label">Diagnosis:</span>

                    <span>{formatText(row.diagnosis)}</span>

                  </div>



                  <div className="sb-main-row">

                    <span className="sb-label">Billed:</span>

                    <span

                      className={`sb-status-pill ${

                        row.billed ? 'sb-status-yes' : 'sb-status-no'

                      }`}

                    >

                      {row.billed ? 'Yes' : 'No'}

                    </span>

                  </div>



                  <div className="sb-main-row">

                    <span className="sb-label">Notes Completed:</span>

                    <span

                      className={`sb-status-pill ${

                        row.notesCompleted ? 'sb-status-yes' : 'sb-status-no'

                      }`}

                    >

                      {row.notesCompleted ? 'Yes' : 'No'}

                    </span>

                  </div>

                </div>



                <div className="superbill-actions">

                  {renderActions(row)}



                  <button

                    className="sb-btn sb-btn-toggle"

                    onClick={() => toggleExpand(row.superbillId)}

                  >

                    {expanded[row.superbillId] ? 'Hide Details' : 'Show Details'}

                  </button>

                </div>



                {expanded[row.superbillId] && (

                  <div className="superbill-details">{renderDetails(row)}</div>

                )}



                {isEditing && renderEditPanel(row)}

              </div>

            );

          })}

        </div>

      ) : (

        <div className="superbill-grid-wrap">

          {rows.length === 0 && !loading ? (

            <div className="superbill-empty">No superbills match this view.</div>

          ) : (

            <table className="superbill-grid-table">

              <thead>

                <tr>

                  {visibleColumns.date && <th>Date</th>}

                  {visibleColumns.patient && <th>Patient</th>}

                  {visibleColumns.dob && <th>DOB</th>}

                  {visibleColumns.insurance && <th>Insurance</th>}

                  {visibleColumns.doctor && <th>Doctor</th>}

                  {visibleColumns.office && <th>Office</th>}

                  {visibleColumns.authorization && <th>Authorization</th>}

                  {visibleColumns.authDates && <th>Auth Dates</th>}

                  {visibleColumns.totalVisits && <th>Total Visits</th>}

                  {visibleColumns.visitsRemaining && <th>Remaining</th>}

                  {visibleColumns.typeVisit && <th>Type Visit</th>}

                  {visibleColumns.procedure && <th>Procedure</th>}

                  {visibleColumns.diagnosis && <th>Diagnosis</th>}

                  {visibleColumns.billed && <th>Billed</th>}

                  {visibleColumns.notesCompleted && <th>Notes</th>}

                  {visibleColumns.actions && <th>Actions</th>}

                </tr>

              </thead>



              <tbody>

                {rows.map((row) => (

                  <React.Fragment key={row.superbillId}>

                    <tr>

                      {visibleColumns.date && (

                        <td>{formatDisplayDate(row.superbillDate)}</td>

                      )}



                      {visibleColumns.patient && (

                        <td>{formatText(row.patientName)}</td>

                      )}



                      {visibleColumns.dob && (

                        <td>{formatDisplayDate(row.dob)}</td>

                      )}



                      {visibleColumns.insurance && (

                        <td>{formatText(row.insuranceName)}</td>

                      )}



                      {visibleColumns.doctor && (

                        <td>{formatText(row.doctorName)}</td>

                      )}



                      {visibleColumns.office && (

                        <td>{formatText(row.officeName)}</td>

                      )}



                      {visibleColumns.authorization && (

                        <td>{getAuthorizationDisplay(row)}</td>

                      )}



                      {visibleColumns.authDates && (

                        <td>{getAuthDatesDisplay(row)}</td>

                      )}



                      {visibleColumns.totalVisits && (

                        <td>{getTotalVisitsDisplay(row)}</td>

                      )}



                      {visibleColumns.visitsRemaining && (

                        <td>{getVisitsRemainingDisplay(row)}</td>

                      )}



                      {visibleColumns.typeVisit && (

                        <td>{formatTypeVisit(row.typeVisit)}</td>

                      )}



                      {visibleColumns.procedure && (

                        <td className="sb-long-cell">

                          {formatText(row.procedure)}

                        </td>

                      )}



                      {visibleColumns.diagnosis && (

                        <td className="sb-long-cell">

                          {formatText(row.diagnosis)}

                        </td>

                      )}



                      {visibleColumns.billed && (

                        <td>

                          <span

                            className={`sb-status-pill ${

                              row.billed ? 'sb-status-yes' : 'sb-status-no'

                            }`}

                          >

                            {row.billed ? 'Yes' : 'No'}

                          </span>

                        </td>

                      )}



                      {visibleColumns.notesCompleted && (

                        <td>

                          <span

                            className={`sb-status-pill ${

                              row.notesCompleted

                                ? 'sb-status-yes'

                                : 'sb-status-no'

                            }`}

                          >

                            {row.notesCompleted ? 'Yes' : 'No'}

                          </span>

                        </td>

                      )}



                      {visibleColumns.actions && <td>{renderActions(row)}</td>}

                    </tr>



                    {editingSuperbillId === row.superbillId && (

                      <tr>

                        <td colSpan="16">{renderEditPanel(row)}</td>

                      </tr>

                    )}

                  </React.Fragment>

                ))}

              </tbody>

            </table>

          )}

        </div>

      )}

      {renderClinicalProfileModal()}

    </div>

  );

};



export default SuperbillPage;